import { onCompletedAction } from '@ifca-root/react-component/src/components/AccountLedger/AllocationPage/AllocationFunction';
import { AllocationPage } from '@ifca-root/react-component/src/components/AccountLedger/AllocationPage/AllocationPage';
import { smallTitle } from '@ifca-root/react-component/src/helpers/Global/ContractGlobalVar';
import { ErrorDialog } from '@ifca-root/react-component/src/components/Dialog/ErrorDialog';
import Loading from '@ifca-root/react-component/src/components/Loading/Loading';
import { useFuseSearch } from '@ifca-root/react-component/src/helpers/Hooks/useSearch';
import SnackBarContext from 'containers/App/Store/SnackBarContext';
import {
  GetAllocationSupplierDocument,
  TransactionStatus,
  useGetAllocationSupplierLazyQuery,
  useMakeSupplierAllocationMutation,
} from 'generated/graphql';
import { usePDFDownload } from 'helpers/Hooks/usePDFDownload';
import React, { useContext, useEffect, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router';

export const SupplierAllocation = ({ transactionType }) => {
  const { setOpenSnackBar, setSnackBarMsg }: any = useContext(SnackBarContext);
  const { transactionID }: any = useParams();
  const { setOriginalListing, filteredList } = useFuseSearch();
  let location = useLocation();
  const [errorDialog, setErrorDialog] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [destructureType, setDestructureType] = useState('');

  const stateInfo: any = location?.state;

  // ROUTING FOR BACK & ON COMPLETE
  const history = useHistory();
  let temp = history?.location?.pathname; // get current path
  let urlBackPath = temp.substring(0, temp.lastIndexOf('/')); // remove the path

  const onClickRoute = () => {
    history.push({
      pathname: urlBackPath.substring(0, urlBackPath.lastIndexOf('/')),
      state: stateInfo,
    });
  };

  // HEADER DATA
  const smTitle = smallTitle.SUPPLIER_ACCOUNTS;
  const routeSegments = [
    { name: 'Main Menu' },
    { name: 'Sub Menu' },
    { name: transactionType, current: true },
  ];

  const [
    loadSupplierAllocation,
    {
      loading: supplierAllocationLoading,
      data: {
        getAPDebitNote,
        getCNtoSupplier,
        getAPInvoice,
        getAPInvoiceWoDO,
      } = {
        getAPDebitNote: [],
        getCNtoSupplier: [],
        getAPInvoice: [],
        getAPInvoiceWoDO: [],
      },
    },
  ] = useGetAllocationSupplierLazyQuery({
    fetchPolicy: 'network-only',
    onError: e => {
      let error = e.message.substring(15);
      setDestructureType('query');
      setErrorDialog(true);
      setErrorMsg(error);
    },
  });

  useEffect(() => {
    if (!!stateInfo?.contractID && !!stateInfo?.supplierID) {
      loadSupplierAllocation({
        variables: {
          contractID: stateInfo?.contractID,
          supplierID: stateInfo?.supplierID,
          creditDebitID: transactionID,
          transactionStatus: TransactionStatus.Approved,
        },
      });
    }
  }, []);

  const setToMapData = (data, table, onClick) => {
    const refTable = {
      Deposits: 'c_APAdvance',
      'DN to Supplier': 'c_DNtoSupplier',
      'Credit Notes': 'c_APCreditNote',
    }[transactionType];

    return data?.map(e => {
      return {
        ...e,
        debitRefTable: refTable,
        debitID: transactionID,
        creditRefTable: table,
        creditID: e?.ID,
        onClick: onClick,
        outstandingAmt: e?.outstandingAmt + e?.allocatedAmt,
      };
    });
  };

  useEffect(() => {
    if (getAPDebitNote && getCNtoSupplier && getAPInvoice && getAPInvoiceWoDO) {
      let allData;
      let tempDebitNote = setToMapData(getAPDebitNote, 'c_APDebitNote', false);
      let tempCN2Supplier = setToMapData(
        getCNtoSupplier,
        'c_CNtoSupplier',
        true,
      );
      let tempAPInv = setToMapData(getAPInvoice, 'c_APInvoice', false);
      let tempAPInvWoDO = setToMapData(getAPInvoiceWoDO, 'c_APInvoice', false);
      allData = [
        ...tempDebitNote,
        ...tempCN2Supplier,
        ...tempAPInv,
        ...tempAPInvWoDO,
      ];
      setOriginalListing(allData?.filter(e => e?.outstandingAmt > 0));
    }
  }, [getAPDebitNote, getCNtoSupplier, getAPInvoice, getAPInvoiceWoDO]);

  const [
    makeSupplierAllocationMutation,
    { loading: makeAllocationLoading, error: makeAllocationError },
  ] = useMakeSupplierAllocationMutation({
    onError: e => {
      let error = e.message.substring(15);
      setDestructureType('mutation');
      setErrorDialog(true);
      setErrorMsg(error);
    },
    onCompleted: data => {
      onCompletedAction(
        'make',
        setOpenSnackBar,
        setSnackBarMsg,
        history,
        urlBackPath,
      );
    },
  });

  const handleOnSubmit = data => {
    let ledgerData: any = [];
    data?.forEach(e => {
      ledgerData.push({
        allocationAmt: e?.allocationAmt,
        creditID: e?.creditID,
        creditRefTable: e?.creditRefTable,
        debitID: e?.debitID,
        debitRefTable: e?.debitRefTable,
        taxRate: e?.taxRate,
      });
    });
    makeSupplierAllocationMutation({
      variables: {
        ledger: ledgerData,
      },
      refetchQueries: [
        {
          query: GetAllocationSupplierDocument,
          variables: {
            contractID: stateInfo?.contractID,
            supplierID: stateInfo?.supplierID,
            creditDebitID: transactionID,
            transactionStatus: TransactionStatus.Approved,
          },
        },
      ],
    });
  };

  const { handleDownloadClick } = usePDFDownload();

  return (
    <>
      {makeAllocationLoading && <Loading />}
      {supplierAllocationLoading && <Loading />}
      <AllocationPage
        header={{ smTitle, routeSegments, onClickRoute }}
        item={{ list: filteredList, handleDownloadClick, handleOnSubmit }}
      />
      <ErrorDialog
        errorDia={errorDialog}
        setErrorDia={setErrorDialog}
        errorMsg={errorMsg}
        errorHeaderMsg={`Supplier Allocation ${
          destructureType === 'mutation' ? 'Access' : 'Display'
        } Error`}
        onclick={() => setErrorDialog(false)}
      />
    </>
  );
};
